/**
 * Where a storefront visit came from, for the visitor beacons
 * (`POST /dashboard/visitor`, `/visitor-subscription`,
 * `/visitor-creator-page`). The backend turns it into the "Sources" panel:
 * `utmSource` wins, else the referrer host ("google.com", "x.com"), else
 * Direct. Self-referrals are resolved server-side too.
 *
 * Attribution is per tab session (first touch): the first page of a visit
 * records its external referrer / tag in sessionStorage, and later pages of
 * the same visit (storefront → product → checkout → thank-you) report that
 * same source instead of falling back to Direct when their own referrer is
 * our page or a payment redirect. An explicit tag on the current URL always
 * wins and becomes the visit's source.
 *
 * Safe in SSR / tests: no window → `{}`; storage failures are ignored.
 */

export type VisitSourcePayload = {
	referrer?: string;
	utmSource?: string;
	/** `utm_medium` / `utm_campaign` of the tagged URL (Revenue by UTM). */
	utmMedium?: string;
	utmCampaign?: string;
};

/** Secondary UTM tags kept with the visit's source. */
const UTM_EXTRA_PARAMS = [
	["utm_medium", "utmMedium"],
	["utm_campaign", "utmCampaign"],
] as const;

export const VISIT_SOURCE_STORAGE_KEY = "pf-visit-source";
/** Query params read as an explicit source tag, in priority order. */
export const VISIT_SOURCE_PARAMS = ["utm_source", "ref", "source"] as const;

const MAX_REFERRER_LENGTH = 2048;
const MAX_UTM_LENGTH = 100;

const clean = (value: unknown, max: number): string | undefined => {
	if (typeof value !== "string") return undefined;
	const trimmed = value.trim();
	return trimmed ? trimmed.slice(0, max) : undefined;
};

const hostOf = (url: string): string | null => {
	try {
		return new URL(url).host.toLowerCase();
	} catch {
		return null;
	}
};

function readCurrent(): VisitSourcePayload {
	const payload: VisitSourcePayload = {};

	const referrer = clean(document.referrer, MAX_REFERRER_LENGTH);
	if (referrer) payload.referrer = referrer;

	const params = new URLSearchParams(window.location.search || "");
	for (const key of VISIT_SOURCE_PARAMS) {
		const value = clean(params.get(key), MAX_UTM_LENGTH);
		if (value) {
			payload.utmSource = value;
			break;
		}
	}
	if (payload.utmSource) {
		for (const [param, field] of UTM_EXTRA_PARAMS) {
			const value = clean(params.get(param), MAX_UTM_LENGTH);
			if (value) payload[field] = value;
		}
	}
	return payload;
}

function readStored(): VisitSourcePayload | null {
	try {
		const raw = window.sessionStorage?.getItem(VISIT_SOURCE_STORAGE_KEY);
		if (!raw) return null;
		const parsed = JSON.parse(raw) as Record<string, unknown>;
		const stored: VisitSourcePayload = {};
		const referrer = clean(parsed?.referrer, MAX_REFERRER_LENGTH);
		const utmSource = clean(parsed?.utmSource, MAX_UTM_LENGTH);
		if (referrer) stored.referrer = referrer;
		if (utmSource) {
			stored.utmSource = utmSource;
			for (const [, field] of UTM_EXTRA_PARAMS) {
				const value = clean(parsed?.[field], MAX_UTM_LENGTH);
				if (value) stored[field] = value;
			}
		}
		return referrer || utmSource ? stored : null;
	} catch {
		return null;
	}
}

function store(payload: VisitSourcePayload): void {
	try {
		window.sessionStorage?.setItem(
			VISIT_SOURCE_STORAGE_KEY,
			JSON.stringify(payload),
		);
	} catch {
		// Private mode / blocked storage: attribution falls back per page.
	}
}

export function visitSourcePayload(): VisitSourcePayload {
	if (typeof window === "undefined" || typeof document === "undefined") {
		return {};
	}
	try {
		const current = readCurrent();

		// An explicit tag on this URL always wins and becomes the visit's source.
		if (current.utmSource) {
			store(current);
			return current;
		}

		// Later pages of the same visit keep the first page's source.
		const stored = readStored();
		if (stored) return stored;

		// First page: remember an external referrer for the rest of the visit.
		const referrerHost = current.referrer ? hostOf(current.referrer) : null;
		if (referrerHost && referrerHost !== window.location.host.toLowerCase()) {
			store(current);
		}
		return current;
	} catch {
		return {};
	}
}
